// ./src/components/features/CreateEventWizard/steps/PartnersStep.tsx
//
// "What can sponsors buy?" — shown when the Partners & sponsors feature is on (ROADMAP A2b #62 P6(f)). The event OFFERS
// packages from the account's catalog (per-event price, how many, how partners pay, applications open or not) in the
// Packages page's own `EventPartnerPackageEditDrawer`.
//
// ⚠⚠ A FIRST-TIME HOST HAS AN EMPTY CATALOG, and that drawer only PICKS from it — so on its own this step would be a dead
//   end. "Create a new package" opens the catalog's own `PartnerPackageEditDrawer` (under its provider: it saves through
//   it and does nothing without it), and the package it makes opens straight in the offer drawer, already picked.
// ⓘ The offer drawer takes its save as a prop, so the step owns the save's error (never the list hook's `lastError`,
//   which a failed READ also sets). Before the Event page step, which offers the "Become a partner" block.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PAGE_SIZE, FEATURE_CODES } from '@/config';
import { PartnerPackagesProvider } from '@/contexts';
import { useCreateGate, useToast } from '@/hooks';
import { OFFERING_SAVE_ACTION, useEventPartnerPackages } from '@/hooks/useEventPartnerPackages';
import {
  EventPartnerPackage,
  EventPartnerPackageInput,
  PartnerPackage,
  byOfferingDisplayOrder,
  offeringAmount,
  offeringCurrency,
  offeringSlotsLeft,
  offeringUsesDefaultPrice,
  partnerPaymentModeLabel,
  WHOLE_SET,
} from '@/utils/api';
import { formatCurrency, runBulkWrites, refusalReason } from '@/utils';
import { reorderByDrag } from '@/utils/reorder';
import { ConfirmModal } from '@/components/generic';
import { EventPartnerPackageEditDrawer } from '@/components/features/EventPartnerPackages';
import { PartnerPackageEditDrawer } from '@/components/features/PartnerPackages';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';

// The in-flight key for an order write — apart from a save, so a drag never spins the edit drawer's Save.
const REORDER_ACTION = 'reorder';

interface OfferDrawerState {
  /** The offering being edited; null = a new one. */
  recordId: string | null;
  /** A NEW offering's package, already picked — one the host just created here. */
  defaultPackage?: PartnerPackage | null;
}

const PartnersStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving, draft } = useEventWizard();
  const { eventPartnerPackages, list, isLoaded, create, update, remove, getById, isCreating, isUpdating } = useEventPartnerPackages({
    initialFilters: { eventId: [eventId] },
    pageSize: WHOLE_SET,
    autoLoad: false,
  });
  // Sponsorship is the `commercial` bundle — the Packages page asks the same.
  const gate = useCreateGate(FEATURE_CODES.COMMERCIAL, { eventId });

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [saveError, setSaveError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<OfferDrawerState | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<EventPartnerPackage | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The order sponsors see them on the "Become a partner" block (owner, 2026-10-01: "let user reorder offered packages"):
  // the server's, else the drop the host just made while its write goes out.
  const sorted = useMemo(() => [...eventPartnerPackages].sort(byOfferingDisplayOrder), [eventPartnerPackages]);
  const [orderOverride, setOrderOverride] = useState<EventPartnerPackage[] | null>(null);
  const [reordering, setReordering] = useState(false);
  const shown = orderOverride ?? sorted;
  const offeredPackageIds = useMemo(
    () => eventPartnerPackages.map((o) => o.partnerPackageId).filter((id): id is string => !!id),
    [eventPartnerPackages],
  );
  const editRecord = drawer?.recordId ? getById(drawer.recordId) ?? null : null;

  const openOffer = (next: OfferDrawerState) => {
    setSaveError(null);
    setDrawer(next);
  };

  // ⚠ The Packages page's own rules (`offering*`): an empty price IS the catalog's (or free), and 0 places is FULL, not
  //   unlimited (review, 2026-10-01).
  const subtitleOf = (o: EventPartnerPackage): string => {
    const amount = offeringAmount(o);
    const price =
      amount === null
        ? t('events:wizard.partners.free', { defaultValue: 'Free' })
        : offeringUsesDefaultPrice(o)
          ? t('events:wizard.partners.catalogPrice', {
              defaultValue: '{{price}} (catalog price)',
              price: formatCurrency(amount, offeringCurrency(o) || undefined),
            })
          : formatCurrency(amount, offeringCurrency(o) || undefined);
    const left = offeringSlotsLeft(o);
    const slots =
      left === null
        ? t('events:wizard.partners.unlimited', { defaultValue: 'Unlimited' })
        : left === 0
          ? t('events:wizard.partners.full', { defaultValue: 'Full' })
          : t('events:wizard.partners.left', { defaultValue: '{{count}} left', count: left });

    return [price, slots, partnerPaymentModeLabel(o.paymentMode)].filter(Boolean).join(' · ');
  };

  const handleSubmit = async (input: EventPartnerPackageInput): Promise<OpResult> => {
    const res = drawer?.recordId ? await update(drawer.recordId, input) : await create(input);

    setSaveError(res.didSucceed() ? null : res);

    return res;
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const { next, updates } = reorderByDrag(shown, result.source.index, result.destination.index);

    if (updates.length === 0) {
      return;
    }

    setOrderOverride(next);
    setReordering(true);

    const results = await runBulkWrites(updates, (u) => update(u.id, { orderNo: u.orderNo }, REORDER_ACTION));
    const failed = results.find((r) => !r.didSucceed());

    setReordering(false);

    // ⚠ A refused write is SAID, and the list goes back to what the server holds (never an order that was not saved).
    if (failed) {
      toast.error(refusalReason(failed) || t('events:wizard.partners.reorderFailed', { defaultValue: 'Could not save the new order.' }));
    }

    await load();
    setOrderOverride(null);
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.partners.intro', {
          defaultValue:
            'Offer sponsorship packages on this event — the price, how many partners can take each, and how they pay. Packages come from your account’s catalog; create one here if you have none yet. Drag them into the order sponsors should see. The Event page step offers a “Become a partner” block, where partners apply while applications are open.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.partners.title', { defaultValue: 'Sponsorship packages' })}
        addLabel={t('events:wizard.partners.add', { defaultValue: 'Offer a package' })}
        onAdd={() => gate.canCreate && openOffer({ recordId: null })}
        gate={gate}
        gateWhat={t('events:wizard.partners.another', { defaultValue: 'another package' })}
        disabled={saving}
        starters={[
          {
            key: 'newPackage',
            label: t('events:wizard.partners.createPackage', { defaultValue: 'Create a new package' }),
            icon: 'Plus',
            onClick: () => setCatalogOpen(true),
          },
        ]}
        isLoaded={isLoaded || !!readError}
        readError={readError}
        onRetry={() => void load()}
        errorTitle={t('events:wizard.partners.loadFailed', { defaultValue: 'Could not load the packages' })}
        operation="eventPartnerPackageList"
        emptyText={t('events:wizard.partners.none', { defaultValue: 'No packages offered yet.' })}
        isEmpty={shown.length === 0}
      >
        <DragDropContext onDragEnd={(result) => void onDragEnd(result)}>
          <Droppable droppableId="wizard-partner-packages">
            {(provided) => (
              <Stack gap="xs" ref={provided.innerRef} {...provided.droppableProps}>
                {shown.map((o, index) => (
                  <Draggable key={o.id} draggableId={String(o.id)} index={index} isDragDisabled={shown.length < 2 || saving || reordering}>
                    {(prov) => (
                      <div ref={prov.innerRef} {...prov.draggableProps}>
                        <WizardItemRow
                          icon="Building"
                          title={o.package?.name || '—'}
                          subtitle={subtitleOf(o)}
                          disabled={saving || reordering}
                          onEdit={() => openOffer({ recordId: o.id as string })}
                          onRemove={() => setPendingRemove(o)}
                          dragHandleProps={shown.length > 1 ? prov.dragHandleProps : null}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      </WizardListSection>

      <EventPartnerPackageEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        eventId={eventId}
        record={editRecord}
        defaultPackage={drawer?.defaultPackage ?? null}
        offeredPackageIds={offeredPackageIds}
        operationError={saveError}
        submitting={isCreating || isUpdating(drawer?.recordId ?? undefined, OFFERING_SAVE_ACTION)}
        onSubmit={handleSubmit}
        onSaved={() => {
          setDrawer(null);
          void load();
        }}
        eventTimezone={draft.timezone || null}
      />

      <PartnerPackageEditDrawer
        opened={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        onCreated={(pkg) => {
          setCatalogOpen(false);

          if (pkg.id) {
            openOffer({ recordId: null, defaultPackage: pkg });
          }
        }}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.partners.removeTitle', { defaultValue: 'Stop offering this package?' })}
        message={t('events:wizard.partners.removeMessage', {
          defaultValue: '“{{name}}” will no longer be offered on this event. It stays in your catalog.',
          name: pendingRemove?.package?.name ?? '',
        })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const PartnersStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  // The catalog's provider is only for the "Create a new package" drawer's save — nothing here lists the catalog.
  return (
    <PartnerPackagesProvider pageSize={DEFAULT_PAGE_SIZE} autoLoad={false}>
      <PartnersStepBody eventId={createdEventId} />
    </PartnerPackagesProvider>
  );
};
